import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ClipboardCheckIcon, InboxIcon } from 'lucide-react';
import { AppBar } from '../../components/AppBar';
import { usePortal } from '../../contexts/PortalContext';
import * as api from '../../api/research';
import type { ReviewerWorkItemRow, ReviewerWorkloadRow } from '../../api/types';
import { formatDateTime } from '../../data/fieldMap';
import { firstNameOf } from '../../data/roles';

/** How a due date reads to the reviewer looking at it today. */
function dueLabel(row: ReviewerWorkItemRow): { text: string; tone: 'warn' | 'soon' | 'plain' } {
  if (row.IsReviewed) return { text: formatDateTime(row.ReviewedOn), tone: 'plain' };
  if (row.DaysLeft === null || row.DaysLeft === undefined)
    return { text: row.DueDate || 'No date set', tone: 'plain' };

  if (row.DaysLeft < 0)
    return {
      text: `${Math.abs(row.DaysLeft)} day${Math.abs(row.DaysLeft) === 1 ? '' : 's'} overdue`,
      tone: 'warn'
    };
  if (row.DaysLeft === 0) return { text: 'Due today', tone: 'warn' };
  if (row.DaysLeft <= 7)
    return { text: `${row.DaysLeft} day${row.DaysLeft === 1 ? '' : 's'} left`, tone: 'soon' };

  return { text: row.DueDate, tone: 'plain' };
}

export function ReviewerQueue() {
  const { sessionToken, user } = usePortal();
  const navigate = useNavigate();

  const [rows, setRows] = useState<ReviewerWorkItemRow[]>([]);
  const [workload, setWorkload] = useState<ReviewerWorkloadRow | null>(null);
  const [includeDone, setIncludeDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!sessionToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.reviewerWorkQueue(sessionToken, includeDone);
      if (!res.ok) setError(res.message);
      else {
        setRows(res.rows);
        setWorkload(res.workload);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your review queue.');
    } finally {
      setLoading(false);
    }
  }, [includeDone, sessionToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = workload?.Pending ?? 0;
  const overdue = workload?.Overdue ?? 0;
  const firstName = firstNameOf(user?.fullName) || 'Reviewer';

  /* The one thing worth doing next. */
  const next = rows.find((row) => !row.IsReviewed);

  return (
    <div className="page">
      <AppBar showHome={false} />
      <main className="container section-gap">
        <div className="stack">
          <section className="hero">
            <div>
              <h1>Reviews assigned to you</h1>
              <p>
                {loading
                  ? 'Loading your assignments…'
                  : pending === 0
                    ? `Nothing is waiting on you right now, ${firstName}.`
                    : `${pending} proposal${pending === 1 ? '' : 's'} awaiting your review` +
                      (overdue > 0
                        ? `, ${overdue} of them past the due date.`
                        : '.')}
              </p>
            </div>
            {next ? (
              <button
                type="button"
                className="btn btn--success"
                onClick={() => navigate(`/reviewer/${next.ResearchCode}`)}
              >
                <ClipboardCheckIcon size={16} aria-hidden="true" />
                Start the most urgent
              </button>
            ) : null}
          </section>

          {error ? (
            <div className="notice notice--error" role="alert">
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          <section className="status-strip" aria-label="Your workload">
            <div className="status-cell">
              <span className="status-key">Awaiting your review</span>
              <span className="status-value">{pending}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Overdue</span>
              <span className={`status-value${overdue > 0 ? ' text-warn' : ''}`}>{overdue}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Due this week</span>
              <span className="status-value">{workload?.DueThisWeek ?? 0}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Completed</span>
              <span className="status-value">{workload?.Completed ?? 0}</span>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Your assignments</h2>
                <p className="card-subtitle">
                  Most urgent first. Overdue reviews are flagged; anything without a due
                  date sits at the end.
                </p>
              </div>
              <label className="checkbox-inline" htmlFor="include-done">
                <input
                  id="include-done"
                  type="checkbox"
                  checked={includeDone}
                  onChange={(e) => setIncludeDone(e.target.checked)}
                />
                Show completed
              </label>
            </div>

            <div className="card-body">
              <div className="table-scroll">
                <table className="data-table" style={{ minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th scope="col">IRB no.</th>
                      <th scope="col" style={{ minWidth: 300 }}>
                        Title
                      </th>
                      <th scope="col">Investigator</th>
                      <th scope="col">Category</th>
                      <th scope="col">Due</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="col-action">
                        Open
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7}>
                          <p className="table-empty">Loading…</p>
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <p className="table-empty">
                            <InboxIcon size={16} aria-hidden="true" />{' '}
                            {includeDone
                              ? 'No proposals have been assigned to you.'
                              : 'Nothing is awaiting your review.'}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => {
                        const due = dueLabel(row);
                        return (
                          <tr key={row.ResearchCode}>
                            <td>{row.IrbNumber ?? `#${row.ResearchCode}`}</td>
                            <td>{row.Title || <em className="muted">Untitled</em>}</td>
                            <td>{row.PiName || row.ResearcherName}</td>
                            <td>{row.ReviewCategory ?? '—'}</td>
                            <td>
                              <span
                                className={
                                  due.tone === 'warn'
                                    ? 'text-warn'
                                    : due.tone === 'soon'
                                      ? 'text-soon'
                                      : 'muted'
                                }
                              >
                                {due.text}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${row.IsReviewed ? 'badge--green' : 'badge--grey'}`}
                              >
                                {row.IsReviewed ? 'Reviewed' : 'Awaiting your review'}
                              </span>
                            </td>
                            <td className="col-action">
                              <button
                                type="button"
                                className={`btn btn--small ${
                                  row.IsReviewed ? 'btn--secondary' : 'btn--primary'
                                }`}
                                onClick={() => navigate(`/reviewer/${row.ResearchCode}`)}
                              >
                                <ClipboardCheckIcon size={14} aria-hidden="true" />
                                {row.IsReviewed ? 'View' : 'Review'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
